// PickUpComplete.js
// Pick Up Draft — Phase D. The real results screen (web port of PickUpCompleteView.swift).
//
// Loads the finished game, marks it viewed, and shows two side-by-side score columns
// (YOU vs opponent). Like iOS, the scores are RECOMPUTED client-side from each roster via
// the verified teamRatingCalculator — only result.winnerID (who won) and result.tiebreaker
// (the note) come from the server. Tapping a column toggles the roster shown below it.
//
// Rosters are slot-ordered (game.rounds template, not chronological pick order) and then laid
// out with the PickUp-specific column ordering, exactly mirroring PickUpCompleteView.
//
// Reachable at /battles/pickup/complete?gameID=<id>.

import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toBlob } from "html-to-image";
import wordmark from "../assets/Rosterable.png";
import { useAuth } from "../managers/AuthContext";
import { RosterCardCondensed } from "../components/DraftResult";
import { loadTeamLogos } from "../managers/teamLogoService";
import {
  loadPickUpGame,
  markPickUpGameViewed,
  opponentID,
  opponentUsername,
  tiebreakerNote,
} from "../managers/pickupService";
import {
  calculateTeamRatingWithChemistry,
  getChemistryBreakdown,
  effectiveScaledPoints,
} from "../managers/teamRatingCalculator";

const APP_THEME = "#d4f53c";
const WIN_GREEN = "#2ed966";
const LOSE_RED = "#ff5a5a";

const SPORTS = [
  { id: "football", label: "Football", hex: "#00b04f" },
  { id: "baseball", label: "Baseball", hex: "#db2626" },
  { id: "basketball", label: "Basketball", hex: "#ff8c00" },
  { id: "hockey", label: "Hockey", hex: "#0da6e8" },
  { id: "soccer", label: "Soccer", hex: "#1a66e6" },
];
const sportMeta = (id) => SPORTS.find((s) => s.id === id) || SPORTS[0];

// Chemistry bonus descriptions come through as "<City> Team Chemistry" or
// "<City> <Year> Connection". On the results screen we just want the team (and year if it's a
// same-year bonus), so strip the trailing verbiage.
function teamLabel(description) {
  return (description || "")
    .replace(/\s+Team Chemistry$/, "")
    .replace(/\s+Connection$/, "")
    .trim();
}

// Reorder a user's picks into the game's round/slot template (not chronological pick order),
// so the column ordering below maps to fixed roster slots. Mirrors slotOrderedRoster.
function slotOrderedRoster(game, userID) {
  const picks = game.picks
    .filter((p) => p.userID === userID)
    .sort((a, b) => a.pickIndex - b.pickIndex);
  const slots = game.rounds.flat();
  const bySlot = {};
  for (const p of picks) (bySlot[p.position] = bySlot[p.position] || []).push(p);

  const players = [];
  const positions = [];
  const used = {};
  for (const slot of slots) {
    const idx = used[slot] || 0;
    const group = bySlot[slot] || [];
    if (idx < group.length) {
      players.push(group[idx].player);
      positions.push(slot);
      used[slot] = idx + 1;
    }
  }
  return { players, positions };
}

function interleaved(l, r, t) {
  const m = Math.max(l.length, r.length);
  const o = [];
  for (let i = 0; i < m; i++) {
    if (i < l.length && l[i] < t) o.push(l[i]);
    if (i < r.length && r[i] < t) o.push(r[i]);
  }
  return o;
}

// PickUp-specific grid ordering (mirrors PickUpCompleteView.getOrderedPlayerIndices —
// distinct from the regular Draft's shared ordering).
function orderedIndices(sportId, players, positions) {
  const t = players.length;
  if (t === 0) return [];
  switch (sportId) {
    case "football": {
      const off = Math.min(6, t);
      const def = Math.max(0, t - 6);
      const mx = Math.max(off, def);
      const o = [];
      for (let r = 0; r < mx; r++) {
        if (r < off && r < t) o.push(r);
        if (r < def) {
          const x = r + 6;
          if (x < t) o.push(x);
        }
      }
      return o;
    }
    case "baseball":
      return interleaved([0, 1, 2, 3, 4, 5], [6, 7, 8, 9, 10, 11], t);
    case "soccer":
      return interleaved([10, 8, 9, 7, 6, 5], [3, 4, 2, 1, 0], t);
    case "basketball":
      return interleaved([0, 1, 2, 3, 4], [5, 6, 7, 8, 9], t);
    case "hockey": {
      const byPos = {};
      for (let i = 0; i < t; i++) {
        const pos = (positions[i] || players[i].position).toUpperCase();
        (byPos[pos] = byPos[pos] || []).push(i);
      }
      const order = ["LW", "C", "RW", "LD", "RD", "G"];
      const left = [];
      const right = [];
      for (const pos of order) {
        const group = byPos[pos] || [];
        if (group.length > 0) left.push(group[0]);
        if (group.length > 1) right.push(group[1]);
      }
      const mx = Math.max(left.length, right.length);
      const o = [];
      for (let r = 0; r < mx; r++) {
        if (r < left.length) o.push(left[r]);
        if (r < right.length) o.push(right[r]);
      }
      return o;
    }
    default:
      return players.map((_, i) => i);
  }
}

export default function PickUpComplete() {
  const { user } = useAuth();
  const uid = user?.uid || null;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const gameID = searchParams.get("gameID") || location.state?.gameID || null;
  // A solo Play CPU game arrives in memory (no Firestore doc, no gameID). When present we
  // render it directly and skip the Firestore load + the markViewed write.
  const localGame = location.state?.localGame || null;

  const [game, setGame] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | notfound
  const [selectedTab, setSelectedTab] = useState(0); // 0 = you, 1 = opponent
  const [shareLabel, setShareLabel] = useState("SHARE");
  const [, bumpLogos] = useState(0); // re-render once the logo URL map resolves
  const captureRef = useRef(null);
  const shareFileRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    // Solo CPU game: use the in-memory object as-is; nothing to fetch or write.
    if (localGame) {
      if (localGame.result) {
        setGame(localGame);
        setStatus("ready");
      } else {
        setStatus("notfound");
      }
      return undefined;
    }
    if (!gameID) {
      setStatus("notfound");
      return undefined;
    }
    (async () => {
      try {
        const g = await loadPickUpGame(gameID);
        if (cancelled) return;
        if (!g || !g.result) {
          setStatus("notfound");
          return;
        }
        setGame(g);
        setStatus("ready");
        if (uid) markPickUpGameViewed(gameID, uid).catch(() => {});
      } catch (_) {
        if (!cancelled) setStatus("notfound");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gameID, uid, localGame]);

  // Warm the team-logo URL map so the roster cards resolve their logos on screen.
  useEffect(() => {
    loadTeamLogos().then(() => bumpLogos((n) => n + 1)).catch(() => {});
  }, []);

  // Pre-generate the shareable PNG once the result is on screen, so the share tap is instant.
  // Captures the outcome + both score columns (toggle-independent), forcing the wordmark and
  // team logos on for the shot — same approach as the Draft / Knockout complete screens.
  useEffect(() => {
    if (status !== "ready") return undefined;
    let cancelled = false;
    async function generate() {
      try {
        await loadTeamLogos().catch(() => {});
        if (document.fonts && document.fonts.ready) await document.fonts.ready;
        await new Promise((r) => setTimeout(r, 600));
        const node = captureRef.current;
        if (cancelled || !node) return;
        const imgs = Array.from(node.querySelectorAll("img"));
        await Promise.all(
          imgs.map(async (img) => {
            if (!(img.complete && img.naturalWidth > 0)) {
              await new Promise((res) => {
                img.addEventListener("load", res, { once: true });
                img.addEventListener("error", res, { once: true });
              });
            }
            if (img.decode) {
              try {
                await img.decode();
              } catch (_) {
                /* ignore */
              }
            }
          })
        );
        if (cancelled || !node) return;
        // The capture node lives off-screen and is permanently styled for the
        // share shot (.rb-capturing), so nothing on the visible screen changes
        // while we snapshot it — no more flash of the hidden roster.
        let blob = null;
        const opts = { pixelRatio: 2, backgroundColor: "#0e1117" };
        // A few passes warms up image embedding (mobile Safari blanks the first pass).
        for (let pass = 0; pass < 3; pass++) {
          blob = await toBlob(node, opts);
          if (cancelled) return;
        }
        if (cancelled || !blob) return;
        const sportId = (game && game.sport) || "pickup";
        shareFileRef.current = new File([blob], `rosterable-${sportId}-pickup.png`, { type: "image/png" });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("Share image generation failed:", err);
      }
    }
    generate();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, gameID]);

  if (status === "loading") {
    return <div style={styles.page}><p style={styles.dim}>Loading results…</p></div>;
  }
  if (status === "notfound" || !game) {
    return (
      <div style={styles.page}>
        <p style={styles.dim}>Results unavailable.</p>
        <button style={styles.backBtn} onClick={() => navigate("/battles")}>BACK TO ROSTER BATTLES</button>
      </div>
    );
  }

  const meta = sportMeta(game.sport);
  const result = game.result;
  const isWinner = result.winnerID === uid;
  const oppID = uid ? opponentID(game, uid) : game.playerBID;

  const mine = slotOrderedRoster(game, uid || game.playerAID);
  const opp = slotOrderedRoster(game, oppID);

  const myRating = calculateTeamRatingWithChemistry(mine.players);
  const myBreak = getChemistryBreakdown(mine.players);
  const oppRating = calculateTeamRatingWithChemistry(opp.players);
  const oppBreak = getChemistryBreakdown(opp.players);

  const sel = selectedTab === 0 ? mine : opp;
  const order = orderedIndices(game.sport, sel.players, sel.positions);
  const myOrder = orderedIndices(game.sport, mine.players, mine.positions);
  const note = tiebreakerNote(result.tiebreaker);
  const forfeited = game.status === "forfeited";

  // Share the pre-generated image: native share sheet (mobile) → PNG download (desktop) →
  // copy text — so every browser does something useful. Mirrors the other complete screens.
  async function handleShare() {
    const file = shareFileRef.current;

    if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: "Rosterable" });
        return;
      } catch (err) {
        if (err && err.name === "AbortError") return;
      }
    }

    if (file) {
      try {
        const url = URL.createObjectURL(file);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        setShareLabel("SAVED!");
        setTimeout(() => setShareLabel("SHARE"), 1500);
        return;
      } catch (_) {
        /* fall through to text */
      }
    }

    const oppName = (uid ? opponentUsername(game, uid) : game.playerBUsername) || "Opponent";
    const text =
      `ROSTERABLE\n` +
      `🆚 PICK UP DRAFT 🆚\n` +
      `${user?.username || "You"}: ${myRating}  vs  ${oppName}: ${oppRating}\n` +
      `${isWinner ? "Victory!" : "Defeat."}\n\n` +
      `Daily roster building at Rosterableapp.com`;

    if (navigator.share && (!navigator.canShare || navigator.canShare({ text }))) {
      try {
        await navigator.share({ title: "Rosterable", text });
        return;
      } catch (err) {
        if (err && err.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(text);
    } catch (_) {
      return;
    }
    setShareLabel("COPIED!");
    setTimeout(() => setShareLabel("SHARE"), 1500);
  }

  return (
    <div style={styles.page}>
      {/* On screen, roster-card logos hide on narrow viewports. The off-screen
          capture node (below) carries .rb-capturing so logos are always present
          in the generated share image. */}
      <style>{`
        @media (max-width: 480px) { .rb-roster-logo { display: none; } }
        .rb-capturing .rb-roster-logo { display: flex !important; }
      `}</style>

      {/* ── On-screen UI (interactive) ─────────────────────────────────────── */}
      <div style={styles.header}>
        <button style={styles.iconBtn} onClick={() => navigate("/battles")}>‹</button>
        <div
          style={{
            ...styles.outcome,
            color: isWinner ? WIN_GREEN : LOSE_RED,
            borderColor: isWinner ? `${WIN_GREEN}55` : `${LOSE_RED}55`,
          }}
        >
          {isWinner ? "YOU WIN" : "YOU LOST"}
          {forfeited && <span style={styles.forfeitTag}> · forfeit</span>}
        </div>
      </div>

      <div style={styles.columns}>
        <ScoreColumn
          username={user?.username || "YOU"}
          rating={myRating}
          chemistry={myBreak.totalChemistryPoints}
          breakdown={myBreak}
          playerCount={mine.players.length}
          isWinner={result.winnerID === uid}
          isSelected={selectedTab === 0}
          hex={meta.hex}
          onTap={() => setSelectedTab(0)}
        />
        <ScoreColumn
          username={uid ? opponentUsername(game, uid) : game.playerBUsername}
          rating={oppRating}
          chemistry={oppBreak.totalChemistryPoints}
          breakdown={oppBreak}
          playerCount={opp.players.length}
          isWinner={result.winnerID === oppID}
          isSelected={selectedTab === 1}
          hex={meta.hex}
          onTap={() => setSelectedTab(1)}
        />
      </div>

      {note && <div style={styles.note}>ⓘ {note}</div>}

      {/* Roster grid for the selected column (tap a column above to switch). */}
      <div style={styles.grid}>
        {order.map((i) =>
          i < sel.players.length && i < sel.positions.length ? (
            <RosterCardCondensed
              key={i}
              player={sel.players[i]}
              position={sel.positions[i]}
              sport={meta}
              index={i}
              hasAppeared
            />
          ) : null
        )}
      </div>

      {/* CPU games only: restart another game in the same sport at the same difficulty.
          localGame is set only for a solo Play CPU game and carries the sport + difficulty. */}
      {localGame && (
        <button
          style={{ ...styles.shareBtn, background: meta.hex, color: "#0c0d10" }}
          onClick={() => navigate(`/battles/pickup/cpu?sport=${game.sport}&difficulty=${game.difficulty}`)}
        >
          ↻ PLAY AGAIN
        </button>
      )}
      <button style={{ ...styles.shareBtn, background: meta.hex, color: "#0c0d10" }} onClick={handleShare}>
        ↑ {shareLabel}
      </button>
      <button style={styles.backBtn} onClick={() => navigate("/battles")}>
        BACK TO ROSTER BATTLES
      </button>

      {/* ── Off-screen share-image source (never visible, never focusable) ────
          The OUTER wrapper carries the off-screen positioning. The INNER node
          (captureRef) stays in normal flow, so html-to-image clones it at the
          origin and renders it correctly. (If the captured node itself were
          positioned at left:-99999px, the clone inherits that offset and paints
          off-canvas → a black image.) Permanently styled for the shot
          (.rb-capturing): wordmark, both score columns, and always YOUR roster,
          independent of the on-screen toggle. */}
      <div style={styles.captureOffscreenWrap} inert aria-hidden="true">
        <div ref={captureRef} className="rb-capturing" style={styles.captureBlock}>
          <img src={wordmark} alt="" style={styles.shareWordmark} />

          <div style={styles.header}>
            <button style={styles.iconBtn}>‹</button>
            <div
              style={{
                ...styles.outcome,
                color: isWinner ? WIN_GREEN : LOSE_RED,
                borderColor: isWinner ? `${WIN_GREEN}55` : `${LOSE_RED}55`,
              }}
            >
              {isWinner ? "YOU WIN" : "YOU LOST"}
              {forfeited && <span style={styles.forfeitTag}> · forfeit</span>}
            </div>
          </div>

          <div style={styles.columns}>
            <ScoreColumn
              username={user?.username || "YOU"}
              rating={myRating}
              chemistry={myBreak.totalChemistryPoints}
              breakdown={myBreak}
              playerCount={mine.players.length}
              isWinner={result.winnerID === uid}
              isSelected
              hex={meta.hex}
              onTap={() => {}}
            />
            <ScoreColumn
              username={uid ? opponentUsername(game, uid) : game.playerBUsername}
              rating={oppRating}
              chemistry={oppBreak.totalChemistryPoints}
              breakdown={oppBreak}
              playerCount={opp.players.length}
              isWinner={result.winnerID === oppID}
              isSelected={false}
              hex={meta.hex}
              onTap={() => {}}
            />
          </div>

          {note && <div style={styles.note}>ⓘ {note}</div>}

          <div style={styles.shareRosterWrap}>
            <div style={{ ...styles.shareRosterLabel, color: meta.hex }}>
              {(user?.username || "YOUR").toUpperCase()}'S ROSTER
            </div>
            <div style={styles.shareGrid}>
              {myOrder.map((i) =>
                i < mine.players.length && i < mine.positions.length ? (
                  <RosterCardCondensed
                    key={i}
                    player={mine.players[i]}
                    position={mine.positions[i]}
                    sport={meta}
                    index={i}
                    hasAppeared
                  />
                ) : null
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScoreColumn({ username, rating, chemistry, breakdown, playerCount, isWinner, isSelected, hex, onTap }) {
  const edge = isWinner ? WIN_GREEN : LOSE_RED;
  const op = isSelected ? (isWinner ? 0.7 : 0.55) : (isWinner ? 0.25 : 0.18);
  // Active bonus rows with a non-zero contribution, highest first.
  const rows = breakdown.activeBonuses
    .map((b) => ({ b, pts: effectiveScaledPoints(b, breakdown.activeBonuses, playerCount) }))
    .filter((x) => x.pts > 0)
    .sort((a, b) => b.pts - a.pts);
  const top = rows.slice(0, 3); // show only the top 3 cities — the rest stay hidden

  return (
    <button
      onClick={onTap}
      style={{
        ...styles.col,
        background: isSelected ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.02)",
        border: `${isSelected ? 2 : 1}px solid ${hexA(edge, op)}`,
      }}
    >
      <ScoreWheel rating={rating} hex={hex} />
      <div style={{ ...styles.colName, color: isSelected ? "#fff" : "rgba(255,255,255,0.5)" }}>{username}</div>
      <div style={styles.chemBox}>
        {/* Always render 3 slots so both columns reserve the same height, filled or not. */}
        {[0, 1, 2].map((slot) => {
          const entry = top[slot];
          if (!entry) {
            return (
              <div key={slot} style={styles.chemRow}>
                <span style={{ ...styles.chemDesc, color: hex }}>
                  {slot === 0 && top.length === 0 ? "No chemistry" : "\u00A0"}
                </span>
              </div>
            );
          }
          return (
            <div key={slot} style={styles.chemRow}>
              <span style={{ ...styles.chemDesc, color: hex }}>{teamLabel(entry.b.description)}</span>
              <span style={{ ...styles.chemPts, color: hex }}>+{entry.pts}</span>
            </div>
          );
        })}
        <div style={{ ...styles.chemTotal, color: hex }}>
          <span>CHEMISTRY</span>
          <span>{chemistry > 0 ? `+${chemistry}` : "+0"}</span>
        </div>
      </div>
    </button>
  );
}

function ScoreWheel({ rating, hex }) {
  const r = 40;
  const circ = 2 * Math.PI * r;
  const dash = circ * Math.min(1, rating / 999);
  return (
    <svg width="88" height="88" viewBox="0 0 88 88" style={{ display: "block", margin: "0 auto" }}>
      <circle cx="44" cy="44" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="9" />
      <circle
        cx="44"
        cy="44"
        r={r}
        fill="none"
        stroke={hex}
        strokeWidth="7"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${circ}`}
        transform="rotate(-90 44 44)"
      />
      <text x="44" y="44" dominantBaseline="central" textAnchor="middle" fontSize="30" fontWeight="700" fill={hex}>
        {rating}
      </text>
    </svg>
  );
}

function hexA(hex, alpha) {
  const a = Math.round(Math.min(1, Math.max(0, alpha)) * 255)
    .toString(16)
    .padStart(2, "0");
  return `${hex}${a}`;
}

const styles = {
  page: { padding: "16px 16px 96px", maxWidth: 560, margin: "0 auto", color: "#e8e9ec", minHeight: "100vh" },
  // Off-screen WRAPPER for the share-image source. ONLY the wrapper is moved
  // off-screen — the captured node (captureBlock) below stays in normal flow so
  // html-to-image clones and renders it at the origin. (If the captured node
  // itself were positioned at left:-99999px, the clone inherits that offset and
  // paints off-canvas → a black image.)
  captureOffscreenWrap: {
    position: "fixed",
    left: "-99999px",
    top: 0,
    width: "480px",
    pointerEvents: "none",
  },
  // The captured node: normal flow (static), fixed width so the generated image
  // is consistent across devices.
  captureBlock: {
    width: "480px",
    padding: "16px",
    boxSizing: "border-box",
    background: "#0e1117",
    display: "flex",
    flexDirection: "column",
  },
  shareWordmark: { height: 22, margin: "0 auto 10px", objectFit: "contain" },
  header: { position: "relative", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 38, marginBottom: 12 },
  iconBtn: { position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "rgba(255,255,255,0.8)", fontSize: 28, cursor: "pointer", width: 32, lineHeight: 1, textAlign: "left", padding: 0 },
  outcome: { width: "50%", textAlign: "center", fontSize: 16, fontWeight: 900, letterSpacing: 1.5, padding: "8px 0", borderRadius: 10, border: "1px solid", whiteSpace: "nowrap" },
  forfeitTag: { fontSize: 13, fontWeight: 600, opacity: 0.7, letterSpacing: 1 },
  columns: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 8 },
  col: { display: "block", width: "100%", borderRadius: 16, padding: "12px 8px 14px", cursor: "pointer", textAlign: "center" },
  colName: { fontSize: 15, fontWeight: 800, fontStyle: "italic", letterSpacing: 0.8, margin: "8px 0 6px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  chemBox: { borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 8, textAlign: "left" },
  chemRow: { display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 3, gap: 6 },
  chemDesc: { fontWeight: 700, color: "rgba(255,255,255,0.55)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  chemPts: { fontWeight: 700, fontFamily: "monospace" },
  chemTotal: { display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 800, letterSpacing: 0.5, marginTop: 6, paddingTop: 6, borderTop: "1px solid rgba(255,255,255,0.08)" },
  note: { textAlign: "center", fontSize: 11, color: "rgba(255,255,255,0.4)", marginBottom: 10 },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 16 },
  // Your roster — rendered inside the off-screen capture node only (never shown
  // on the interactive screen), so the share image always reveals your own team.
  shareRosterWrap: { marginTop: 10 },
  shareRosterLabel: { fontSize: 11, fontWeight: 800, letterSpacing: 1.5, textAlign: "center", margin: "2px 0 8px" },
  shareGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 },
  shareBtn: { width: "100%", padding: "14px", borderRadius: 12, border: "none", fontWeight: 800, letterSpacing: 1, cursor: "pointer", fontSize: 14, marginBottom: 10 },
  backBtn: { width: "100%", padding: "14px", borderRadius: 12, border: "none", background: "#1a1d24", color: "#e8e9ec", fontWeight: 800, letterSpacing: 1, cursor: "pointer", fontSize: 14 },
  dim: { fontSize: 14, color: "#6e747e", textAlign: "center", margin: "40px 0 16px" },
};
